import axios from 'axios';
import {RENEWALS_TYPES} from '@/views/RenovaDashboard/RenewalsParts/constants';
import {cloneDeep} from 'lodash';

const defaultFilters = {
  searchQuery: '',
  groupingTypes: [],
  renewalStages: [],
  transactionStatuses: [],
  agentUserIds: [],
  insuranceType: [],
  insuranceCompanyId: [],
};
const state = {
  renewals: {
    items: [],
    loading: false,
  },
  triggers: [],
  filteredRenewalsList: [],
  filters: cloneDeep(defaultFilters),
};

const getters = {
  renewalList: () => state.renewals.items,
  triggersList: () => state.triggers,
  filteredRenewalsList: () => state.filteredRenewalsList,
  renewalsLoading: () => state.renewals.loading,
  renewalsFilters: () => state.filters,
};
export function fetchTrigger(triggerId) {
  return axios
    .post('/insurance/policy/renewal/trigger/detail', { triggerId });
}
export function fetchTriggerHistory(triggerId) {
  return axios
    .post('/insurance/policy/renewal/trigger/history/list', { triggerId });
}
export function retryTriggerHistoryItem(triggerId, itemId) {
  return axios
    .post('/insurance/policy/renewal/trigger/history/retry', { triggerId, itemId });
}
// Every renewals trigger's history at once — the Activity log. Filters go to the server, which also
// decides which rows this operator is allowed to see.
export function fetchTriggerActivity(payload) {
  return axios
    .post('/insurance/policy/renewal/trigger/history/all', payload);
}
export function retryTriggerActivityBulk(payload) {
  return axios
    .post('/insurance/policy/renewal/trigger/history/retry-bulk', payload);
}
const actions = {
  fetchRenewals({commit, state}) {
    commit('setRenewalsListLoading', true);
    axios.post('/insurance/policy/renewal/list', {
      page: 0,
      limit: 1000,
      ...state.filters,
    }).then((response) => {
      commit('setRenewals', response.data);
      commit('updateFilteredRenewals');
      commit('setRenewalsListLoading', false);
    }).catch((error) => {
      console.error('Error fetching renewals:', error);
      commit('setRenewalsListLoading', false);
    });
  },

  updateRenewal({commit, dispatch}, renewal) {
    axios.post(`/insurance/${renewal.packageId ? 'package' : 'policy'}/save`, {
      ...renewal
    })
      .then((response) => {
        dispatch('fetchRenewals');
      })
      .catch((error) => {
        console.error('Error updating renewals:', error);
      });
  },
  renewPolicy({commit, dispatch}, renewal) {
    axios.post('/insurance/policy/renew', {
      ...renewal
    })
      .then((response) => {
        dispatch('fetchRenewals');
      })
      .catch((error) => {
        console.error('Error updating renewals:', error);
      });
  },
  linkRenewedPolicy({ dispatch }, { oldPolicyId, newPolicyId }) {
    return axios.post('/insurance/policy/renew/link', {
      oldPolicyId,
      newPolicyId,
    })
      .then((response) => {
        dispatch('fetchRenewals');
        return response;
      })
      .catch((error) => {
        console.error('Error linking renewals:', error);
        throw error;
      });
  },
  exportRenewals({commit, dispatch}, renewals) {
    axios.post('/insurance/renewals/download', {
      renewals
    })
      .then((resp) => {
        const fileName = 'Renewals';

        const link = document.createElement('a');
        link.href = URL.createObjectURL(new Blob([resp.data], {
          type: 'text/csv',
        }));
        link.download = fileName;
        link.click();

        URL.revokeObjectURL(link.href);
      })
      .catch((error) => {
        console.error('Error fetching renewal details:', error);
      });
  },
  updateFilters({commit, dispatch}, filters) {
    commit('setRenewalsFilters', filters);
    dispatch('fetchRenewals');
    // commit('updateFilteredRenewals');
  },
  fetchTriggers({commit, state}) {
    commit('setRenewalsListLoading', true);
    return axios.post('/insurance/policy/renewal/trigger/list', {}).then((response) => {
      commit('setTriggers', response.data);
    }).catch((error) => {
      console.error('Error fetching renewals:', error);
    });
  },
  createTrigger({commit, dispatch}, trigger) {
    commit('setRenewalsListLoading', true);
    return axios.post('/insurance/policy/renewal/trigger/save', trigger).then((response) => {
      dispatch('fetchRenewals', response.data);
      dispatch('fetchTriggers', response.data);
    }).catch((error) => {
      console.error('Error fetching renewals:', error);
      throw new Error(error);
    });
  },
  // Like createTrigger, but the caller sees the backend's own error (the Clara rules answer with
  // a message the operator can act on).
  saveRenewalTrigger({dispatch}, trigger) {
    return axios.post('/insurance/policy/renewal/trigger/save', trigger).then((response) => {
      dispatch('fetchRenewals');
      dispatch('fetchTriggers');
      return response;
    });
  },
  deleteTrigger({commit, dispatch}, triggerId) {
    commit('setRenewalsListLoading', true);
    return axios
      .post('/insurance/policy/renewal/trigger/delete', { triggerId })
      .then((response) => {
        dispatch('fetchRenewals', response.data);
        dispatch('fetchTriggers', response.data);
      })
      .catch((error) => {
        throw new Error(error);
      });
  },
};

const mutations = {
  setRenewals(state, renewals) {
    state.renewals.items = cloneDeep([...renewals.items]);
  },
  setTriggers(state, triggers) {
    state.triggers = cloneDeep([...triggers.items]);
  },
  setRenewalsListLoading(state, loading) {
    state.renewals.loading = loading;
  },

  // A policy inside both renewal windows gets the distinct RENEWAL_1_2 stage, and the backend
  // matches stages exactly — so picking "within 30 days" has to ask for RENEWAL_1_2 as well or
  // those policies vanish from the board. Rebuilt as a new array every time: the previous version
  // push()ed into the live array on *every* filter change, which both accumulated duplicates and,
  // because the state was only a shallow copy of defaultFilters, permanently polluted the defaults
  // that clearAllFilters hands back.
  setRenewalsFilters(state, filters) {
    const updatedFilters = {...state.filters, ...filters};
    const stages = (updatedFilters.renewalStages || []).filter(
      (val) => val !== RENEWALS_TYPES.WITHIN_TWO_PERIODS
    );
    const needsBothPeriods = stages.includes(RENEWALS_TYPES.WITHIN_FIRST_PERIOD)
      || stages.includes(RENEWALS_TYPES.WITHIN_SECOND_PERIOD);
    updatedFilters.renewalStages = needsBothPeriods
      ? [...stages, RENEWALS_TYPES.WITHIN_TWO_PERIODS]
      : stages;
    state.filters = updatedFilters;
  },

  clearAllFilters(state) {
    state.filters = cloneDeep(defaultFilters);
  },
  updateFilteredRenewals() {
    state.filteredRenewalsList = state.renewals.items;
  }
};

export default {
  state,
  getters,
  actions,
  mutations,
};
